import React, { useEffect, useState } from "react";
import PageHero from "../components/PageHero";
import { EditableText, EditableImage } from "../components/live/Editable";
import api from "../lib/api";
import { Handshake, ExternalLink, Globe, Video } from "lucide-react";

const HWK_CENTERS = [
  "Center for Alimentation and Health",
  "Construction Center",
  "Center for Environment and Labour Safety",
  "Center for Design, Design & Communication",
  "Center for Plastic and Colour",
  "Metal & Technology Center",
];

const HWK_LINKS = [
  { label: "German website", href: "http://hwk-koblenz.de", icon: Globe },
  { label: "Global website", href: "https://www.hwk-koblenz.de/artikel/auslandsprojekte-52,0,231.html#asien", icon: ExternalLink },
  { label: "Videos", href: "https://goo.gl/OXLjPo", icon: Video },
];

const OTHER_PARTNERS = [
  { name: "Mitsubishi Electric Systems", role: "Training Partner", img: "/assets/kiki/partner-mitsubishi.jpg" },
  { name: "Moldex 3D", role: "Training Partner", img: "/assets/kiki/partner-moldex.jpg" },
  { name: "Alumniportal", role: "Relevant Links", img: "/assets/kiki/partner-alumni.jpg" },
];

export default function PartnersPage() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/partners", { params: { active_only: true } }).then((r) => setItems(r.data));
  }, []);

  return (
    <>
      <PageHero
        path="partners.hero"
        kicker="Our Partners"
        title="Partner"
        subtitle="From the German Chamber of Skilled Crafts to global training partners — our network powers your growth."
      />

      {/* HWK Koblenz feature */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="flex items-center gap-4 mb-6">
              <img
                src="/assets/kiki/partner-hwk-logo.png"
                alt="Handwerkskammer Koblenz logo"
                className="h-16 w-auto rounded-lg bg-white border border-slate-100 shadow-sm p-2"
              />
              <EditableText path="partners.hwk.title" as="h2" className="text-3xl font-bold text-[#0B2A6B]">Handwerkskammer Koblenz</EditableText>
            </div>
            <EditableText
              path="partners.hwk.text1"
              as="p"
              multiline
              className="text-slate-600 leading-relaxed mb-5 block"
              data-testid="partners-hwk-text"
            >
              Since 2011, Kiki Centre for Technology has engaged in a partnership with the German Chamber of Skilled Crafts, Koblenz, Germany (HWK, Koblenz, Germany). HWK Koblenz supports Kiki with the implementation of the Earn While You Learn courses according to German standards and supports in adaptation of the German curriculum from training right upto certification.
            </EditableText>
            <EditableText path="partners.hwk.text2" as="p" multiline className="text-slate-600 leading-relaxed mb-5 block">
              Training certified through HWK, Koblenz, follows the high German quality standards — both in terms of equipment and also training content. At the moment 3 courses are covered in the professions of:
            </EditableText>
            <ul className="space-y-2 mb-7">
              {["Mechanical – Tool and Die Making", "Mechanical – Precision Machinist", "Electrical & Electronics – Mechatronics"].map((p, i) => (
                <li key={p} className="flex items-center gap-3 bg-yellow-50 border border-yellow-100 rounded-xl px-4 py-3 font-semibold text-[#0B2A6B]">
                  <Handshake className="h-5 w-5 text-yellow-600 shrink-0" />
                  <EditableText path={`partners.hwk.profession.${i}`} as="span">{p}</EditableText>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3">
              {HWK_LINKS.map((l) => (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B2A6B] text-white text-sm font-semibold hover:bg-[#08205a] transition"
                  data-testid={`partners-hwk-link-${l.label.toLowerCase().replace(/ /g, "-")}`}
                >
                  <l.icon className="h-4 w-4 text-yellow-400" /> {l.label}
                </a>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            <EditableImage
              path="partners.main.image"
              src="/assets/kiki/partner-main.jpg"
              alt="Kiki Centre partnership with HWK Koblenz"
              className="rounded-3xl shadow-xl overflow-hidden"
              imgClassName="w-full h-full object-cover"
              data-testid="partners-main-image"
            />
            <div className="bg-slate-50 rounded-2xl border border-slate-100 p-7">
              <EditableText path="partners.hwk.centersTitle" as="h3" className="font-bold text-[#0B2A6B] text-lg mb-4 block">In Germany HWK operates a number of centers, namely:</EditableText>
              <ul className="grid sm:grid-cols-2 gap-2.5">
                {HWK_CENTERS.map((c, i) => (
                  <li key={c} className="flex items-start gap-2 text-sm text-slate-700">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-yellow-400 shrink-0" />
                    <EditableText path={`partners.hwk.center.${i}`} as="span">{c}</EditableText>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Training partners & relevant links */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="kicker mb-3">Training Partners & Relevant Links</p>
            <h2 className="text-3xl font-bold text-[#0B2A6B]">Working with global technology leaders</h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {OTHER_PARTNERS.map((p, i) => (
              <div
                key={p.name}
                className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm text-center tilt-card animate-fade-up"
                style={{ animationDelay: `${i * 0.07}s` }}
                data-testid={`partners-card-${i}`}
              >
                <div className="h-20 flex items-center justify-center mb-4">
                  <EditableImage
                    path={`partners.other.${i}.image`}
                    src={p.img}
                    alt={p.name}
                    className="h-16 w-full flex items-center justify-center"
                    imgClassName="max-h-16 w-auto object-contain mx-auto"
                  />
                </div>
                <EditableText path={`partners.other.${i}.name`} as="h3" className="font-bold text-[#0B2A6B] block">{p.name}</EditableText>
                <EditableText path={`partners.other.${i}.role`} as="p" className="text-sm text-slate-500 mt-1 block">{p.role}</EditableText>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full partner network from CMS */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="kicker mb-3">Our Network</p>
            <h2 className="text-3xl font-bold text-[#0B2A6B]">Our partner network</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((p, i) => (
              <div
                key={p.id}
                className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm tilt-card animate-fade-up"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                <div className="h-14 w-14 rounded-xl bg-yellow-400 flex items-center justify-center mb-3">
                  <Handshake className="h-7 w-7 text-[#0B2A6B]" />
                </div>
                <h3 className="font-bold text-[#0B2A6B] text-lg">{p.name}</h3>
                {p.role && <p className="text-sm text-slate-600 mt-1">{p.role}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
